import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api, setMeta } from "@/lib/museo";
import { track } from "@/lib/analytics";
import { Counter } from "@/components/Counter";
import { PromiseCard } from "@/components/PromiseCard";
import { PromiseModal } from "@/components/PromiseModal";
import { Footer } from "@/components/Footer";

const PAGE = 50;

export default function Home() {
  const [stats, setStats] = useState({ esposte: 0, cap: 100000, completo: false });
  const [promises, setPromises] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setMeta({
      title: "Il Museo delle Promesse — 10.000 posti per il tuo messaggio",
      description: "Solo 10.000 posti nel museo digitale collettivo. Incidi il tuo messaggio, anonimo o firmato, con un numero unico. Per sempre.",
      canonical: `${window.location.origin}/`,
      ogType: "website",
    });
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const fetchPromises = useCallback(async (q, sk, append) => {
    setLoading(true);
    try {
      const r = await api.get("/promises", { params: { q, skip: sk, limit: PAGE } });
      setTotal(r.data.total);
      setPromises((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
    } catch {
      if (!append) setPromises([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromises("", 0, false);
  }, [fetchPromises]);

  const onSearch = (val) => {
    setQuery(val);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPromises(val.trim(), 0, false);
    }, 300);
  };

  const loadMore = () => {
    const next = skip + PAGE;
    setSkip(next);
    fetchPromises(query.trim(), next, true);
  };

  return (
    <div className="relative z-10 min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12 flex flex-col items-center text-center gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="flex flex-col items-center gap-6"
        >
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-museo-text leading-tight">
            Il Museo delle Promesse
          </h1>
          <p className="text-base md:text-lg text-museo-text font-light tracking-wide">
            Solo 10.000 posti per lasciare il tuo messaggio inciso per sempre.
          </p>
          <p className="font-serif italic text-xl md:text-2xl text-museo-text/80 max-w-2xl leading-relaxed">
            “Una promessa, una scusa, un addio, un perdono. Alcune parole meritano un posto.”
          </p>
          <p className="text-sm md:text-base text-museo-muted font-light tracking-wide">
            Anonimo o firmato. Visibile a tutti. Con un numero unico nel museo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="flex flex-col items-center gap-8 mt-4"
        >
          {stats.completo ? (
            <div data-testid="museo-completo" className="px-8 py-4 border border-museo-border text-museo-accent font-serif text-xl">
              Il Museo delle Promesse è completo.
            </div>
          ) : (
            <button
              data-testid="cta-lascia-promessa"
              onClick={() => {
                track("incidi_click", { location: "hero" });
                setModalOpen(true);
              }}
              className="bg-museo-accent text-museo-bg px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-museo-text transition-all duration-500 rounded-none md:rounded-sm"
            >
              Incidi il tuo messaggio — 1,99 €
            </button>
          )}
          <Counter value={stats.esposte} cap={stats.cap} />
        </motion.div>
      </section>

      {/* Search */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 mb-20">
        <div className="relative">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 md:w-6 md:h-6 text-museo-accent" />
          <input
            data-testid="search-input"
            type="text"
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Cerca una parola, un nome, figlio, amore, mamma…"
            className="w-full bg-transparent border-b-2 border-museo-border text-museo-text text-lg md:text-2xl py-4 pl-9 md:pl-11 placeholder:text-museo-muted/40 focus:border-museo-accent focus:outline-none transition-colors duration-300 font-serif"
          />
        </div>
      </section>

      {/* Wall */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        {promises.length === 0 && !loading ? (
          <p data-testid="no-results" className="text-center text-museo-muted font-light py-16">
            {query ? "Nessun messaggio trovato." : "Nessun messaggio ancora. Sii il primo."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {promises.map((p, i) => (
              <PromiseCard key={p.numero} promise={p} index={i % PAGE} />
            ))}
          </div>
        )}

        {!query && promises.length < total && (
          <div className="flex justify-center mt-16">
            <button
              data-testid="load-more-button"
              onClick={loadMore}
              disabled={loading}
              className="bg-transparent border border-museo-border text-museo-text px-10 py-4 text-sm tracking-[0.1em] uppercase hover:border-museo-accent transition-all duration-500 rounded-none md:rounded-sm disabled:opacity-40"
            >
              {loading ? "Caricamento…" : "Carica altre"}
            </button>
          </div>
        )}
      </section>

      <Footer />
      <PromiseModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
